import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Info, ChevronRight, Check, Sparkles, Shield, CheckCircle, Target, ArrowRight, X, Plus, AlertTriangle, Loader2, Monitor, Trash2, RefreshCw, Clock, BarChart3, Cloud, Server, Users, FileText, Brain } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { getCurrentSubdomain } from '../utils/sessionManager';
import type { OnboardingState } from '../types/phase1e';
import type { ProjectGoals, CurrentSystem, PainPoint, Objective, SuccessMetric, TimelinePhase } from '../types/projectGoals';
import { emptyProjectGoals } from '../types/projectGoals';
import { toast } from 'sonner';
import { cachedFetch } from '../utils/fetchWithCache';
import type { NextStep } from '../utils/clientHome';
import { AiWaitingNote } from './AiWaitingNote';
import { categoryLabel } from '../utils/discoveryGrid';
import { hasRole, isSystemAdmin } from '../utils/roleUtils';

interface AdaptiveOnboardingProps {
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  onComplete: () => void;
  /** Pre-loaded data from parent bundle -- skips own fetches when provided */
  initialOnboardingState?: OnboardingState | null;
  initialPreFilledData?: any | null;
}

interface TeamMember {
  name: string;
  email: string;
  role: 'team_member' | 'engagement_lead' | 'executive_sponsor';
}

interface HLOData {
  currentSystem?: string;
  painPoints: string;
  desiredBenefits: string;
  timeline?: string;
  successMetrics?: string;
}

/**
 * What the six-step OnboardingFlow asked beyond the goals (RESTORE_PLAN R2.3): platform type,
 * go-live urgency, team size and departments. Saved as `client_profile` beside the project goals.
 */
export interface ClientProfile {
  platformType: 'cloud' | 'datacenter' | null;
  urgency: 'urgent' | 'standard' | 'flexible' | null;
  teamSize: string;
  departmentsInvolved: string;
}

export const emptyClientProfile = (): ClientProfile => ({ platformType: null, urgency: null, teamSize: '', departmentsInvolved: '' });

export const URGENCY_OPTIONS: Array<{ value: NonNullable<ClientProfile['urgency']>; label: string; hint: string }> = [
  { value: 'urgent', label: 'Urgent', hint: 'As soon as possible' },
  { value: 'standard', label: 'Standard', hint: 'A normal pace' },
  { value: 'flexible', label: 'Flexible', hint: 'When it is ready' },
];

export const TEAM_SIZE_OPTIONS = ['1-5', '6-20', '21-50', '51-200', '200+'];

export function AdaptiveOnboarding({ 
  companyId, 
  companyName,
  accessToken, 
  currentUser, 
  onComplete,
  initialOnboardingState,
  initialPreFilledData,
}: AdaptiveOnboardingProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step data (declared early — referenced by session/draft effects below)
  const [aiEnabled, setAiEnabled] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([{ name: '', email: '', role: 'team_member' }]);
  const [projectGoals, setProjectGoals] = useState<ProjectGoals>(emptyProjectGoals());
  const [clientProfile, setClientProfile] = useState<ClientProfile>(emptyClientProfile());
  const [hloData, setHloData] = useState<HLOData>({
    currentSystem: '',
    painPoints: '',
    desiredBenefits: '',
    timeline: '',
    successMetrics: '',
  });
  const [preFilledData, setPreFilledData] = useState<{
    hasSOW: boolean;
    hasBDMNotes: boolean;
    consultantName?: string;
    preFilled: string[];
  }>({
    hasSOW: false,
    hasBDMNotes: false,
    preFilled: [],
  });

  // ============================================
  // SESSION HEARTBEAT (every 60s, with startup grace period)
  // ============================================
  const sessionExpiredRef = useRef(false); // one-shot guard to prevent cascading
  const mountTimeRef = useRef(Date.now());

  const checkSession = useCallback(async (): Promise<boolean> => {
    // Extract sessionId from the accessToken (sess_... format)
    const sessionId = accessToken?.startsWith('sess_') || accessToken?.startsWith('imp_') ? accessToken : null;
    if (!sessionId) {
      console.log('[ONBOARDING_HEARTBEAT] No session token available, skipping check');
      return true; // no token to validate — not an expiry
    }

    // Same subdomain the app's boot check uses. On root hosts such as
    // staging.pathkit.pro it is null, and the backend skips the firm match;
    // the hostname's first label ("staging") would never match the firm.
    const subdomain = getCurrentSubdomain();

    const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/session/validate`;
    const method = 'POST';
    const body = JSON.stringify({ sessionId, subdomain });

    console.log(`[ONBOARDING_HEARTBEAT] Checking session: ${method} ${url} (subdomain=${subdomain}, tokenPrefix=${sessionId.substring(0, 10)}...)`);

    try {
      const response = await fetch(url, {
        method,
        headers: getAuthHeadersWithJson(accessToken),
        body,
      });

      console.log(`[ONBOARDING_HEARTBEAT] Response status=${response.status}`);

      // ONLY 401/403 = confirmed auth expiry
      if (response.status === 401 || response.status === 403) {
        console.warn('[ONBOARDING_HEARTBEAT] Session EXPIRED (confirmed auth failure)');
        return false;
      }

      // Check response body for explicit valid:false
      if (response.ok) {
        try {
          const data = await response.json();
          if (data.valid === false) {
            console.warn('[ONBOARDING_HEARTBEAT] Session EXPIRED (valid:false in response)');
            return false;
          }
        } catch { /* non-JSON 200 is fine */ }
        console.log('[ONBOARDING_HEARTBEAT] Session VALID');
        return true;
      }

      // 404/500/502/503 = transient/implementation error, NOT expiry
      console.warn(`[ONBOARDING_HEARTBEAT] Non-auth error (status=${response.status}), treating as VALID (not expired)`);
      return true;
    } catch (err) {
      console.warn('[ONBOARDING_HEARTBEAT] Network error (not treating as expired):', err);
      return true;
    }
  }, [accessToken]);

  useEffect(() => {
    // Grace period: skip heartbeat for first 10s after mount to let auth/restore settle
    const STARTUP_GRACE_MS = 10_000;
    const HEARTBEAT_INTERVAL_MS = 60_000;

    const interval = setInterval(async () => {
      // Don't check if already expired (one-shot guard)
      if (sessionExpiredRef.current) return;
      // Don't check during startup grace period
      if (Date.now() - mountTimeRef.current < STARTUP_GRACE_MS) {
        console.log('[ONBOARDING_HEARTBEAT] Skipping check during startup grace period');
        return;
      }

      const valid = await checkSession();
      if (!valid && !sessionExpiredRef.current) {
        sessionExpiredRef.current = true;
        saveDraft(companyId, { step: currentStep, projectGoals, teamMembers, hloData, clientProfile, timestamp: Date.now() });
        setSessionExpired(true);
        toast.error('Your session has expired. Your work has been saved locally.');
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkSession, companyId, currentStep, projectGoals, teamMembers, hloData]);

  // ============================================
  // DRAFT AUTOSAVE (debounced 2s after changes)
  // ============================================
  useEffect(() => {
    if (!companyId || loading) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      saveDraft(companyId, { step: currentStep, projectGoals, teamMembers, hloData, clientProfile, timestamp: Date.now() });
      console.log('[ONBOARDING_DRAFT] Autosaved draft');
    }, 2000);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [companyId, currentStep, projectGoals, teamMembers, hloData, loading]);

  // ============================================
  // DRAFT RESTORE ON MOUNT
  // ============================================
  const draftRestored = useRef(false);
  useEffect(() => {
    if (loading || draftRestored.current) return;
    draftRestored.current = true;
    const draft = loadDraft(companyId);
    if (draft && draft.timestamp > Date.now() - 24 * 60 * 60 * 1000) {
      console.log('[ONBOARDING_DRAFT] Found local draft from', new Date(draft.timestamp).toLocaleString());
      // Restore draft data
      if (draft.projectGoals) setProjectGoals(draft.projectGoals);
      if (draft.teamMembers?.length) setTeamMembers(draft.teamMembers);
      if (draft.hloData) setHloData(draft.hloData);
      if (draft.clientProfile) setClientProfile({ ...emptyClientProfile(), ...draft.clientProfile });
      if (draft.step >= 0 && draft.step < 6) setCurrentStep(draft.step);
      toast.info('Restored your saved draft from ' + new Date(draft.timestamp).toLocaleTimeString());
    }
  }, [loading, companyId]);

  // ============================================
  // COMPANY AI SWITCH (step 2): the same company.aiEnabled that Company Settings edits.
  // Loaded from the server, saved when the user leaves the step. The server refuses
  // every AI call for the company while it is off (openai.ts).
  // ============================================
  const canChangeAi = (hasRole(currentUser, 'companyAdmin') && currentUser?.companyId === companyId) || isSystemAdmin(currentUser);
  const savedAiEnabledRef = useRef<boolean | null>(null); // last value the server holds; null until loaded
  const aiTouchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/preferences`,
          { headers: getAuthHeaders(accessToken) },
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const value = data?.preferences?.aiEnabled !== false;
        savedAiEnabledRef.current = value;
        if (!aiTouchedRef.current) setAiEnabled(value);
      } catch (error) {
        console.warn('[ONBOARDING] Could not load the company AI setting:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, accessToken]);

  const changeAiEnabled = (value: boolean) => {
    aiTouchedRef.current = true;
    setAiEnabled(value);
  };

  const saveAiPreference = async () => {
    if (!canChangeAi) return;
    // Unknown server value and untouched switch: saving the default could turn AI back on.
    if (savedAiEnabledRef.current === null && !aiTouchedRef.current) return;
    if (aiEnabled === savedAiEnabledRef.current) return;
    try {
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/preferences`,
        { method: 'PATCH', headers: getAuthHeadersWithJson(accessToken), body: JSON.stringify({ aiEnabled }) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      savedAiEnabledRef.current = aiEnabled;
      toast.success(aiEnabled ? 'AI assistance is on for your company' : 'AI assistance is off for your company');
    } catch (error) {
      console.error('[ONBOARDING] Could not save the company AI setting:', error);
      toast.error('Could not save the AI setting. You can change it in Company Settings.');
    }
  };

  const goBack = async () => {
    // The AI switch is saved whichever way the user leaves the welcome step (Back is disabled there; kept for safety)
    if (steps[currentStep]?.id === 'welcome') await saveAiPreference();
    setCurrentStep(Math.max(0, currentStep - 1));
  };

  // Save current step to localStorage whenever it changes
  useEffect(() => {
    if (companyId) {
      const storageKey = `onboarding_step_${companyId}`;
      localStorage.setItem(storageKey, currentStep.toString());
    }
  }, [currentStep, companyId]);
  
  // Four steps (ledger L08; the client walk of 2026-10-28 asked for four): the welcome carries
  // the AI switch (the old "Company Info" step, S11), the Insights step moved to the consultant's
  // client page (L3), and the last step is the starting point. The saved state keeps its six
  // keys: companyInfo completes with welcome, discovery completes with setup.
  const steps = [
    { id: 'welcome', title: 'Welcome', canSkip: false },
    // Goals, metrics and phases are the consultant's to author; a client may skip them.
    { id: 'hlos', title: 'Project Goals', canSkip: true },
    { id: 'teamInvites', title: 'Team Members', canSkip: true },
    { id: 'review', title: 'Done', canSkip: false },
  ];
  const CLIENT_STEP_KEYS: Array<keyof OnboardingState['steps']> = ['welcome', 'hlos', 'teamInvites', 'review'];

  useEffect(() => {
    loadOnboardingState();
  }, [companyId]);

  const loadOnboardingState = async () => {
    setLoading(true);
    try {
      // -------------------------------------------------------------------
      // FAST PATH: Use pre-loaded data from parent bundle (0 HTTP calls)
      // -------------------------------------------------------------------
      let state: OnboardingState;
      if (initialOnboardingState !== undefined && initialOnboardingState !== null) {
        console.log('[ADAPTIVE ONBOARDING] Using pre-loaded onboarding state from parent bundle');
        state = initialOnboardingState;
      } else {
        // SLOW PATH: fetch individually (fallback)
        const stateResponse = await cachedFetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/onboarding-state`,
          {
            headers: getAuthHeaders(accessToken),
          }
        );

        if (stateResponse.ok) {
          state = await stateResponse.json();
        } else {
          state = createInitialState();
        }
      }

      // Pre-filled data
      if (initialPreFilledData) {
        console.log('[ADAPTIVE ONBOARDING] Using pre-loaded pre-filled data from parent bundle');
        setPreFilledData(initialPreFilledData);
        if (initialPreFilledData.preFilled?.includes('companyInfo')) {
          state.steps.companyInfo.completed = true;
          state.steps.companyInfo.completedBy = 'consultant';
          state.steps.companyInfo.completedByName = initialPreFilledData.consultantName || 'Your consultant';
        }
      } else {
        // SLOW PATH: fetch individually (fallback)
        const preFilledResponse = await cachedFetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/pre-filled-data`,
          {
            headers: getAuthHeaders(accessToken),
          }
        );

        if (preFilledResponse.ok) {
          const preFilled = await preFilledResponse.json();
          setPreFilledData(preFilled);
          
          // Skip company info if consultant already filled it
          if (preFilled.preFilled.includes('companyInfo')) {
            state.steps.companyInfo.completed = true;
            state.steps.companyInfo.completedBy = 'consultant';
            state.steps.companyInfo.completedByName = preFilled.consultantName || 'Your consultant';
          }
        }
      }

      setOnboardingState(state);
      
      // Check if onboarding is already completed
      if (state.currentStep === 'completed') {
        console.log('[ADAPTIVE ONBOARDING] Onboarding already completed, calling onComplete()');
        setLoading(false);
        onComplete();
        return;
      }
      
      // Restore current step from localStorage, or find first incomplete step
      const storageKey = `onboarding_step_${companyId}`;
      const savedStep = localStorage.getItem(storageKey);
      
      if (savedStep !== null) {
        const stepNumber = parseInt(savedStep, 10);
        // Make sure it's a valid step number
        if (stepNumber >= 0 && stepNumber < steps.length) {
          setCurrentStep(stepNumber);
        } else {
          // Fallback to first incomplete
          const firstIncomplete = findFirstIncompleteStep(state);
          setCurrentStep(firstIncomplete);
        }
      } else {
        // No saved step, find first incomplete
        const firstIncomplete = findFirstIncompleteStep(state);
        setCurrentStep(firstIncomplete);
      }

    } catch (error) {
      console.error('[ONBOARDING] Error loading state:', error);
      toast.error('Failed to load onboarding');
    } finally {
      setLoading(false);
    }
  };

  const createInitialState = (): OnboardingState => ({
    companyId,
    steps: {
      welcome: { completed: false },
      companyInfo: { completed: false },
      hlos: { completed: false },
      teamInvites: { completed: false },
      discovery: { completed: false },
      review: { completed: false },
    },
    currentStep: 'welcome',
    overallProgress: 0,
    canSkipOnboarding: false,
    lastUpdated: new Date().toISOString(),
    version: 1,
  });

  const findFirstIncompleteStep = (state: OnboardingState): number => {
    const stepOrder = CLIENT_STEP_KEYS;
    for (let i = 0; i < stepOrder.length; i++) {
      const stepKey = stepOrder[i] as keyof OnboardingState['steps'];
      if (!state.steps[stepKey].completed) {
        return i;
      }
    }
    return 0;
  };

  const saveStepProgress = async (stepId: keyof OnboardingState['steps']) => {
    if (!onboardingState) return;

    setSaving(true);
    try {
      const updatedState: OnboardingState = {
        ...onboardingState,
        steps: {
          ...onboardingState.steps,
          [stepId]: {
            completed: true,
            completedAt: new Date().toISOString(),
            completedBy: currentUser.id,
            completedByName: currentUser.name || currentUser.email,
          },
        },
        lastUpdated: new Date().toISOString(),
        version: onboardingState.version + 1,
      };

      // The AI switch lives on the welcome step; the saved shape keeps its old key.
      if (stepId === 'welcome') updatedState.steps.companyInfo = { ...updatedState.steps.welcome };

      // Calculate progress over the four steps the client sees
      const completedSteps = CLIENT_STEP_KEYS.filter((key) => updatedState.steps[key]?.completed).length;
      updatedState.overallProgress = Math.round((completedSteps / CLIENT_STEP_KEYS.length) * 100);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/onboarding-state`,
        {
          method: 'PUT',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify(updatedState),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save progress');
      }

      setOnboardingState(updatedState);
      toast.success('Progress saved');
    } catch (error) {
      console.error('[ONBOARDING] Error saving:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // PRE-SUBMIT SESSION GUARD (only block on confirmed auth failure)
    if (!sessionExpiredRef.current) {
      const valid = await checkSession();
      if (!valid) {
        sessionExpiredRef.current = true;
        saveDraft(companyId, { step: currentStep, projectGoals, teamMembers, hloData, clientProfile, timestamp: Date.now() });
        setSessionExpired(true);
        toast.error('Session expired. Your draft has been saved locally. Please sign in again.');
        return;
      }
    } else {
      // Already expired, don't proceed
      return;
    }

    const currentStepId = steps[currentStep].id as keyof OnboardingState['steps'];
    
    // Save step-specific data
    if (currentStepId === 'welcome') {
      await saveAiPreference();
    } else if (currentStepId === 'hlos') {
      await saveHLOData();
    } else if (currentStepId === 'teamInvites') {
      await saveTeamInvites();
    }

    // Mark step as complete
    await saveStepProgress(currentStepId);

    // Move to next step
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Onboarding complete!
      await completeOnboarding();
    }
  };

  const handleSkip = async () => {
    // Don't mark the step as complete when skipping - just move forward
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      toast.success('Step skipped');
    }
  };

  const saveHLOData = async () => {
    try {
      // Save canonical ProjectGoals via PUT endpoint (source of truth)
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/engagement-goals-context`,
        {
          method: 'PUT',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            project_goals: projectGoals,
            client_profile: clientProfile,
            updatedBy: currentUser.id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save project goals');
      }

      // Also save legacy hloData for backward compat with other consumers
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/hlos`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            source: 'client_input',
            data: hloData,
            submittedBy: currentUser.id,
            submittedByName: currentUser.name || currentUser.email,
          }),
        }
      ).catch(err => console.warn('[ONBOARDING] Legacy hloData save failed (non-blocking):', err));

      toast.success('Project goals saved');
    } catch (error) {
      console.error('[ONBOARDING] Error saving project goals:', error);
      toast.error('Failed to save project goals');
      throw error;
    }
  };

  /** Team size and departments (the Team Members step) go on the same client profile. */
  const saveClientProfile = async () => {
    const { platformType, urgency, teamSize, departmentsInvolved } = clientProfile;
    if (!platformType && !urgency && !teamSize.trim() && !departmentsInvolved.trim()) return;
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/engagement-goals-context`,
        {
          method: 'PUT',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({ client_profile: clientProfile, updatedBy: currentUser.id }),
        }
      );
      if (!response.ok) throw new Error('Failed to save your team details');
    } catch (error) {
      console.error('[ONBOARDING] Error saving the client profile:', error);
      toast.error('Could not save your team details');
    }
  };

  const saveTeamInvites = async () => {
    await saveClientProfile();
    const validMembers = teamMembers.filter(m => m.name && m.email);
    if (validMembers.length === 0) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/invite-team`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            teamMembers: validMembers,
            invitedBy: currentUser.id,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invites');
      }

      const invited: number = data.invited ?? 0;
      const failed: Array<{ email: string; error?: string }> = data.failed ?? [];
      if (invited > 0) {
        toast.success(`Invites sent to ${invited} team member${invited > 1 ? 's' : ''}`);
      }
      for (const f of failed) {
        toast.error(`${f.email}: ${f.error || 'could not be invited'}`);
      }
    } catch (error) {
      console.error('[ONBOARDING] Error sending invites:', error);
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to send team invites');
      throw error;
    }
  };

  const completeOnboarding = async () => {
    try {
      if (!onboardingState) return;

      // Mark review step as complete and set overall state to completed
      const completedState: OnboardingState = {
        ...onboardingState,
        // Setup is done: every step counts as complete, the skipped ones and the two the client
        // no longer sees included (the dashboard shows onboarding until every step is complete).
        steps: Object.fromEntries(
          Object.entries(onboardingState.steps).map(([key, step]) => [
            key,
            step?.completed ? step : { completed: true, completedAt: new Date().toISOString(), completedBy: currentUser.id, completedByName: currentUser.name || currentUser.email },
          ]),
        ) as OnboardingState['steps'],
        currentStep: 'completed',
        overallProgress: 100,
        lastUpdated: new Date().toISOString(),
        version: onboardingState.version + 1,
      };

      // Save the completed state to the server
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/onboarding-state`,
        {
          method: 'PUT',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify(completedState),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save onboarding completion');
      }

      // Clear localStorage step tracking + draft
      const storageKey = `onboarding_step_${companyId}`;
      localStorage.removeItem(storageKey);
      clearDraft(companyId);

      // OnboardingFlow's finish ran the goal-gap analysis so the consultant finds suggested items
      // waiting for approval; the server runs it in the background and a failure only logs.
      fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/onboarding-finish`,
        { method: 'POST', headers: getAuthHeadersWithJson(accessToken) },
      ).catch((err) => console.warn('[ONBOARDING] gap analysis trigger failed (non-blocking):', err));

      toast.success('Onboarding complete');
      onComplete();
    } catch (error) {
      console.error('[ONBOARDING] Error completing onboarding:', error);
      toast.error('Failed to complete onboarding');
    }
  };

  const addTeamMember = () => {
    setTeamMembers([...teamMembers, { name: '', email: '', role: 'team_member' }]);
  };

  const removeTeamMember = (index: number) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const updateTeamMember = (index: number, field: keyof TeamMember, value: string) => {
    const updated = [...teamMembers];
    updated[index] = { ...updated[index], [field]: value };
    setTeamMembers(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  const progress = onboardingState ? onboardingState.overallProgress : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-8">
      {/* SESSION EXPIRED MODAL */}
      {sessionExpired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Session Expired</h3>
                <p className="text-sm text-gray-500">Your login session has ended</p>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                Your draft has been saved locally. After signing in, you'll pick up where you left off.
              </p>
            </div>
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={() => { window.location.href = '/login'; }}
            >
              Sign In Again
            </Button>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-gray-900">Getting Started</h2>
            <span className="text-sm text-gray-600">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          
          {/* Integrated Step Progress */}
          <div className="relative">
            {/* Background line */}
            <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200 rounded-full" style={{ marginLeft: '16px', marginRight: '16px' }} />
            
            {/* Progress line */}
            <div 
              className="absolute top-4 left-0 h-1 bg-gradient-to-r from-purple-600 to-purple-500 rounded-full transition-all duration-500"
              style={{ 
                marginLeft: '16px',
                width: `calc(${(currentStep / (steps.length - 1)) * 100}% - 32px)`
              }}
            />
            
            {/* Step circles */}
            <div className="relative flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-300 relative z-10 ${
                      index < currentStep
                        ? 'bg-green-500 text-white shadow-md'
                        : index === currentStep
                        ? 'bg-purple-600 text-white shadow-lg scale-110'
                        : 'bg-white border-2 border-gray-300 text-gray-400'
                    }`}
                  >
                    {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <span className={`text-xs mt-2 text-center max-w-[80px] ${
                    index === currentStep ? 'text-purple-700 font-medium' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 0 && <WelcomeStep preFilledData={preFilledData} aiEnabled={aiEnabled} setAiEnabled={changeAiEnabled} canChange={canChangeAi} isCompanyAdmin={hasRole(currentUser, 'companyAdmin')} />}
        {currentStep === 1 && (
          <HLOStep
            projectGoals={projectGoals}
            setProjectGoals={setProjectGoals}
            hloData={hloData}
            setHloData={setHloData}
            clientProfile={clientProfile}
            setClientProfile={setClientProfile}
            companyId={companyId}
            accessToken={accessToken}
          />
        )}
        {currentStep === 2 && (
          <TeamInvitesStep
            teamMembers={teamMembers}
            addTeamMember={addTeamMember}
            removeTeamMember={removeTeamMember}
            updateTeamMember={updateTeamMember}
            clientProfile={clientProfile}
            setClientProfile={setClientProfile}
          />
        )}
        {currentStep === 3 && <ReviewStep companyName={companyName} companyId={companyId} accessToken={accessToken} />}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={currentStep === 0 || saving}
          >
            Back
          </Button>
          <div className="flex gap-2">
            {steps[currentStep].canSkip && (
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={saving}
              >
                Skip for Now
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Saving...' : currentStep === steps.length - 1 ? 'Complete Setup' : currentStep === 1 ? 'Confirm & Continue' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step Components

function WelcomeStep({ preFilledData, aiEnabled, setAiEnabled, canChange, isCompanyAdmin }: { preFilledData: any; aiEnabled: boolean; setAiEnabled: (v: boolean) => void; canChange: boolean; isCompanyAdmin: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Welcome to PathKit</CardTitle>
        <CardDescription>
          Begin your onboarding
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-700">
          We'll guide you through a quick onboarding to understand your goals and gather the information your consultant needs.
        </p>

        {preFilledData.hasBDMNotes && (
          <Alert className="border-blue-200 bg-blue-50">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">Pre-populated Data</AlertTitle>
            <AlertDescription className="text-blue-700">
              Your consultant {preFilledData.consultantName && `(${preFilledData.consultantName})`} has already shared information about your project. Pre-filled fields will be used to reduce setup time.
            </AlertDescription>
          </Alert>
        )}

        <div className="bg-purple-50 rounded-lg p-4 space-y-2">
          <h4 className="text-purple-900">What to expect:</h4>
          <ul className="space-y-1 text-purple-700 text-sm ml-4">
            <li className="flex items-start gap-2">
              <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Share your project goals and objectives</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Invite your team members (optional)</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Begin guided discovery with your consultant</span>
            </li>
          </ul>
        </div>

        <AiAssistanceBlock aiEnabled={aiEnabled} setAiEnabled={setAiEnabled} canChange={canChange} />

        {isCompanyAdmin && <AdminTips />}

        <p className="text-sm text-gray-600">
          About five minutes. You can save your progress and come back anytime.
        </p>
      </CardContent>
    </Card>
  );
}

/** The two tips WelcomeMessage gave a new company administrator (RESTORE_PLAN R2.3, F2), in today's words. */
function AdminTips() {
  return (
    <div className="space-y-3" data-testid="admin-tips">
      <h4 className="text-gray-900">As your company's administrator you can:</h4>
      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
        <Users className="h-5 w-5 text-gray-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-gray-900">Manage users</p>
          <p className="text-sm text-gray-600">Invite teammates on the Team Members step, and add or remove people later from your company settings.</p>
        </div>
      </div>
      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
        <FileText className="h-5 w-5 text-gray-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-gray-900">Shape your request forms</p>
          <p className="text-sm text-gray-600">Your answers in the Discovery Grid become the request forms, fields, teams and workflows your consultant builds for you.</p>
        </div>
      </div>
    </div>
  );
}

function AiAssistanceBlock({ aiEnabled, setAiEnabled, canChange }: { aiEnabled: boolean; setAiEnabled: (v: boolean) => void; canChange: boolean }) {
  // The AI switch, on the welcome step (it was a step of its own titled "Company Info", S11).
  // The data-protection text stays, behind "How we protect your data".
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
        <div className="flex-1">
          <Label htmlFor="ai-enabled" className="text-gray-900">
            Enable AI Assistance
          </Label>
          <div className="flex items-center gap-1 mt-1">
            <p className="text-sm text-gray-600">
              Get intelligent suggestions, faster data collection, and proactive insights
            </p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{canChange ? 'You can change this setting anytime in Company Settings' : 'Your company admin manages this setting in Company Settings'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <Switch
          id="ai-enabled"
          checked={aiEnabled}
          onCheckedChange={setAiEnabled}
          disabled={!canChange}
        />
      </div>

      <details className="rounded-lg border border-purple-200 bg-purple-50 p-4">
        <summary className="cursor-pointer text-purple-900 flex items-center gap-2">
          <Shield className="h-4 w-4 text-purple-600" />
          How we protect your data
        </summary>
        {/* AI control and AI call logging lines are left out until the company AI switch
            gates every AI feature and AI calls are logged; the AI gating task restores them. */}
        <ul className="list-disc ml-4 mt-3 space-y-1 text-sm text-purple-700">
          <li><strong>Encryption:</strong> All data is encrypted in transit (TLS) and at rest by our database provider. PathKit also encrypts API tokens you enter to connect other tools, and saved AI context, with AES-256-GCM.</li>
          <li><strong>Data Separation:</strong> Each consulting firm's data is kept separate, and our server checks your access on every request. Browsers cannot read the database directly.</li>
          <li><strong>Deletion &amp; Export:</strong> Your company and all of its data can be permanently deleted on request, and its configuration can be exported. To request a copy of your personal data or its deletion, email privacy@pathkit.pro.</li>
        </ul>
        <p className="text-xs text-purple-700 mt-3">
          See exactly what we send to AI and how it's handled —{' '}
          <a
            href="/ai-trust"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-purple-900"
          >
            PathKit AI Data Use
          </a>.
        </p>
      </details>
    </div>
  );
}

function HLOStep({ 
  projectGoals, 
  setProjectGoals, 
  hloData, 
  setHloData, 
  clientProfile,
  setClientProfile,
  companyId,
  accessToken
}: { 
  projectGoals: ProjectGoals; 
  setProjectGoals: (data: ProjectGoals) => void;
  hloData: HLOData; 
  setHloData: (data: HLOData) => void;
  clientProfile: ClientProfile;
  setClientProfile: (p: ClientProfile) => void;
  companyId: string;
  accessToken: string;
}) {
  const [contextLoading, setContextLoading] = useState(true);
  const [sourceLabels, setSourceLabels] = useState<string[]>([]);
  const [hasUpstreamData, setHasUpstreamData] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Free-text goals, turned into objectives by the AI beside the drafted ones (R2.3, OnboardingFlow step 2).
  const [goalsText, setGoalsText] = useState('');
  const [extracting, setExtracting] = useState(false);

  const addObjectivesFromText = async () => {
    const text = goalsText.trim();
    if (!text) { toast.error('Describe your goals first'); return; }
    setExtracting(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/goals-from-text`,
        { method: 'POST', headers: getAuthHeadersWithJson(accessToken), body: JSON.stringify({ text, existing: projectGoals.objectives }) },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not read your goals');
      const added: Objective[] = Array.isArray(data.objectives) ? data.objectives : [];
      if (added.length === 0) {
        toast.info('Nothing new there: those goals are already in the list.');
        return;
      }
      setProjectGoals({ ...projectGoals, objectives: [...projectGoals.objectives, ...added] });
      setGoalsText('');
      toast.success(`Added ${added.length} objective${added.length === 1 ? '' : 's'} from your words`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read your goals');
    } finally {
      setExtracting(false);
    }
  };

  useEffect(() => {
    fetchGoalsContext();
  }, [companyId]);

  // Sync projectGoals → hloData (one-way, backward compat)
  useEffect(() => {
    const systems = projectGoals.current_systems.map(s => s.name).join(', ');
    const pains = projectGoals.pain_points.map(p => `• ${p.description}`).join('\n');
    const objectives = projectGoals.objectives.map(o => `• ${o.description}`).join('\n');
    const tl = projectGoals.timeline.target_go_live || projectGoals.timeline.phases.map(p => `${p.name}: ${p.target_date}`).join(', ');
    const metrics = projectGoals.success_metrics.map(m => `${m.name}: ${m.target}`).join(', ');
    setHloData({
      currentSystem: systems,
      painPoints: pains,
      desiredBenefits: objectives,
      timeline: tl,
      successMetrics: metrics,
    });
  }, [projectGoals]);

  const fetchGoalsContext = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setContextLoading(true);
    try {
      // Refresh rebuilds from the current SOW and notes; a plain load takes what is stored.
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/engagement-goals-context${isRefresh ? '?force=1' : ''}`;
      const response = await fetch(url, { headers: getAuthHeaders(accessToken) });
      const data = await response.json();
      if (data.success && data.context?.project_goals) {
        const pg = data.context.project_goals as ProjectGoals;
        setProjectGoals(pg);
        if (data.context.client_profile) setClientProfile({ ...emptyClientProfile(), ...data.context.client_profile });
        setHasUpstreamData(true);
        const labels: string[] = [];
        if (data.context.inputSources?.hasSow) labels.push('SOW');
        if (data.context.inputSources?.hasHlos) labels.push(`${data.context.inputSources.hloCount} HLOs`);
        if (data.context.inputSources?.hasSalesNotes) labels.push('Sales Notes');
        setSourceLabels(labels);
        console.log('[HLO_STEP] Canonical context loaded:', data.context.source, data._cached ? '(cached)' : '');
      } else {
        console.log('[HLO_STEP] No canonical context, using empty structure');
        setHasUpstreamData(false);
      }
    } catch (err) {
      console.error('[HLO_STEP] Failed to fetch goals context:', err);
    } finally {
      setContextLoading(false);
      setRefreshing(false);
    }
  };

  // === Inline editing helpers ===
  const updateSystem = (index: number, field: keyof CurrentSystem, value: string) => {
    const updated = [...projectGoals.current_systems];
    updated[index] = { ...updated[index], [field]: value };
    setProjectGoals({ ...projectGoals, current_systems: updated });
  };
  const addSystem = () => {
    setProjectGoals({ ...projectGoals, current_systems: [...projectGoals.current_systems, { name: '', usage: 'secondary', notes: '' }] });
  };
  const removeSystem = (index: number) => {
    setProjectGoals({ ...projectGoals, current_systems: projectGoals.current_systems.filter((_, i) => i !== index) });
  };

  const updatePainPoint = (index: number, field: keyof PainPoint, value: string) => {
    const updated = [...projectGoals.pain_points];
    updated[index] = { ...updated[index], [field]: value } as PainPoint;
    setProjectGoals({ ...projectGoals, pain_points: updated });
  };
  const addPainPoint = () => {
    setProjectGoals({ ...projectGoals, pain_points: [...projectGoals.pain_points, { description: '', category: 'technology', severity: 'medium', source: 'inferred' }] });
  };
  const removePainPoint = (index: number) => {
    setProjectGoals({ ...projectGoals, pain_points: projectGoals.pain_points.filter((_, i) => i !== index) });
  };

  const updateObjective = (index: number, field: keyof Objective, value: string) => {
    const updated = [...projectGoals.objectives];
    updated[index] = { ...updated[index], [field]: value } as Objective;
    setProjectGoals({ ...projectGoals, objectives: updated });
  };
  const addObjective = () => {
    setProjectGoals({ ...projectGoals, objectives: [...projectGoals.objectives, { description: '', type: 'efficiency', priority: 'medium', source: 'hlo' }] });
  };
  const removeObjective = (index: number) => {
    setProjectGoals({ ...projectGoals, objectives: projectGoals.objectives.filter((_, i) => i !== index) });
  };

  const updateMetric = (index: number, field: keyof SuccessMetric, value: string) => {
    const updated = [...projectGoals.success_metrics];
    updated[index] = { ...updated[index], [field]: value } as SuccessMetric;
    setProjectGoals({ ...projectGoals, success_metrics: updated });
  };
  const addMetric = () => {
    setProjectGoals({ ...projectGoals, success_metrics: [...projectGoals.success_metrics, { name: '', target: '', baseline: '', confidence: 'low', source: 'inferred' }] });
  };
  const removeMetric = (index: number) => {
    setProjectGoals({ ...projectGoals, success_metrics: projectGoals.success_metrics.filter((_, i) => i !== index) });
  };

  const updatePhase = (index: number, field: keyof TimelinePhase, value: string) => {
    const phases = [...projectGoals.timeline.phases];
    phases[index] = { ...phases[index], [field]: value };
    setProjectGoals({ ...projectGoals, timeline: { ...projectGoals.timeline, phases } });
  };
  const addPhase = () => {
    setProjectGoals({ ...projectGoals, timeline: { ...projectGoals.timeline, phases: [...projectGoals.timeline.phases, { name: '', scope: '', target_date: '' }] } });
  };
  const removePhase = (index: number) => {
    setProjectGoals({ ...projectGoals, timeline: { ...projectGoals.timeline, phases: projectGoals.timeline.phases.filter((_, i) => i !== index) } });
  };

  const severityColor = (s: string) => s === 'high' ? 'bg-red-50 text-red-700 border-red-200' : s === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200';
  const priorityColor = (p: string) => p === 'high' ? 'bg-purple-50 text-purple-700 border-purple-200' : p === 'medium' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200';
  const confidenceColor = (c: string) => c === 'high' ? 'bg-green-50 text-green-700 border-green-200' : c === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200';

  const objectiveTypeLabels: Record<string, string> = {
    efficiency: 'Efficiency', reliability: 'Reliability', experience: 'Experience',
    cost: 'Cost', compliance: 'Compliance', scalability: 'Scalability',
  };

  if (contextLoading) {
    return (
      <Card>
        <CardContent>
          <AiWaitingNote
            title="Our AI is reading your SOW and your consultant's notes to draft your goals"
            detail="Usually a few seconds. You will see what we heard, and you can change any of it."
          />
        </CardContent>
      </Card>
    );
  }

  const pg = projectGoals;
  const cs = pg.confidence_summary;

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-900">
              {hasUpstreamData ? 'Validate & Refine Project Goals' : 'Define Project Goals'}
            </CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-0.5">
              {hasUpstreamData
                ? `Pre-populated from ${sourceLabels.join(', ') || 'discovery inputs'}`
                : 'Tell us about your project goals'}
            </CardDescription>
          </div>
          {hasUpstreamData && (
            <Button variant="outline" size="sm" onClick={() => fetchGoalsContext(true)} disabled={refreshing} className="text-xs">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {hasUpstreamData && (
          <Alert className="border-purple-200 bg-purple-50">
            <Sparkles className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-purple-700 text-sm">
              We extracted these goals from your engagement data. Edit any field inline, then confirm to continue.
            </AlertDescription>
          </Alert>
        )}

        {/* ========== SECTION: Current Systems ========== */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Monitor className="h-4 w-4 text-gray-500" />
            Current Systems
          </h4>
          <div className="space-y-2">
            {pg.current_systems.map((sys, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                <Input
                  className="flex-1 border-0 p-0 h-auto text-sm font-medium shadow-none focus-visible:ring-0"
                  value={sys.name}
                  onChange={(e) => updateSystem(i, 'name', e.target.value)}
                  placeholder="System name"
                />
                <Select value={sys.usage} onValueChange={(v: any) => updateSystem(i, 'usage', v)}>
                  <SelectTrigger className="w-[120px] h-7 text-xs border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="fallback">Fallback</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500" onClick={() => removeSystem(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addSystem} className="text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add System
            </Button>
          </div>
        </div>

        {/* ========== SECTION: Pain Points ========== */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-gray-500" />
            Pain Points
          </h4>
          <div className="space-y-2">
            {pg.pain_points.map((pp, i) => (
              <div key={i} className="flex flex-wrap items-start gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                <Select value={pp.severity} onValueChange={(v: any) => updatePainPoint(i, 'severity', v)}>
                  <SelectTrigger className="w-[100px] h-7 text-xs shrink-0 mt-0.5 pr-7">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-semibold ${severityColor(pp.severity)}`}>
                      {pp.severity}
                    </Badge>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <textarea
                  className="flex-1 min-w-0 basis-48 border-0 p-0 text-sm shadow-none bg-transparent resize-none outline-none focus:ring-0 whitespace-pre-wrap break-words"
                  value={pp.description}
                  onChange={(e) => updatePainPoint(i, 'description', e.target.value)}
                  placeholder="Describe pain point..."
                  rows={1}
                  onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                />
                <Select value={pp.category} onValueChange={(v: any) => updatePainPoint(i, 'category', v)}>
                  <SelectTrigger className="w-[130px] h-7 text-xs shrink-0 pr-7">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="process">Process</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="people">People</SelectItem>
                    <SelectItem value="data">Data</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500 shrink-0" onClick={() => removePainPoint(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addPainPoint} className="text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Pain Point
            </Button>
          </div>
        </div>

        {/* ========== SECTION: Objectives ========== */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Target className="h-4 w-4 text-gray-500" />
            Objectives
          </h4>
          <div className="space-y-2">
            {pg.objectives.map((obj, i) => (
              <div key={i} className="flex flex-wrap items-start gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                <Select value={obj.priority} onValueChange={(v: any) => updateObjective(i, 'priority', v)}>
                  <SelectTrigger className="w-[100px] h-7 text-xs shrink-0 mt-0.5 pr-7">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-semibold ${priorityColor(obj.priority)}`}>
                      {obj.priority}
                    </Badge>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <textarea
                  className="flex-1 min-w-0 basis-48 border-0 p-0 text-sm shadow-none bg-transparent resize-none outline-none focus:ring-0 whitespace-pre-wrap break-words"
                  value={obj.description}
                  onChange={(e) => updateObjective(i, 'description', e.target.value)}
                  placeholder="Describe objective..."
                  rows={1}
                  onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                />
                <Select value={obj.type} onValueChange={(v: any) => updateObjective(i, 'type', v)}>
                  <SelectTrigger className="w-[130px] h-7 text-xs shrink-0 pr-7">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(objectiveTypeLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500 shrink-0" onClick={() => removeObjective(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addObjective} className="text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Objective
            </Button>
          </div>

          {/* In your own words → objectives (OnboardingFlow step 2) */}
          <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4 space-y-3" data-testid="goals-from-text">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-purple-600" />
              <h5 className="text-sm font-semibold text-purple-900">{pg.objectives.length > 0 ? 'Anything missing? Say it in your own words' : 'Describe your goals in your own words'}</h5>
            </div>
            <Textarea
              value={goalsText}
              onChange={(e) => setGoalsText(e.target.value)}
              rows={4}
              className="bg-white resize-none"
              placeholder="Example: It takes us about 24 hours to respond to tickets and we want that down to 4. We also want better visibility into recurring issues, and to automate common requests like password resets."
              data-testid="goals-text"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-purple-700">Structure is not needed. The AI turns what matters to you into objectives you can edit above.</p>
              <Button size="sm" onClick={addObjectivesFromText} disabled={extracting || !goalsText.trim()} className="bg-purple-600 hover:bg-purple-700 text-xs">
                {extracting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Reading…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Turn into objectives</>}
              </Button>
            </div>
          </div>
        </div>

        {/* ========== SECTION: Success Metrics ========== */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-gray-500" />
            Success Metrics
          </h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Baseline</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[100px]">Confidence</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {pg.success_metrics.map((m, i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 px-3">
                      <Input className="border-0 p-0 h-auto text-sm shadow-none focus-visible:ring-0" value={m.name} onChange={(e) => updateMetric(i, 'name', e.target.value)} placeholder="Metric name" />
                    </td>
                    <td className="py-1.5 px-3">
                      <Input className="border-0 p-0 h-auto text-sm shadow-none focus-visible:ring-0" value={m.target} onChange={(e) => updateMetric(i, 'target', e.target.value)} placeholder="Target value" />
                    </td>
                    <td className="py-1.5 px-3">
                      <Input className="border-0 p-0 h-auto text-sm shadow-none focus-visible:ring-0" value={m.baseline} onChange={(e) => updateMetric(i, 'baseline', e.target.value)} placeholder="Current baseline" />
                    </td>
                    <td className="py-1.5 px-3">
                      <Select value={m.confidence} onValueChange={(v: any) => updateMetric(i, 'confidence', v)}>
                        <SelectTrigger className="h-7 text-xs border-0 shadow-none p-0">
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-semibold ${confidenceColor(m.confidence)}`}>
                            {m.confidence}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 px-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500" onClick={() => removeMetric(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={addMetric} className="text-xs mt-2">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Metric
          </Button>
        </div>

        {/* ========== SECTION: Timeline ========== */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            Timeline
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider shrink-0">Go-Live</Label>
              <Input
                className="flex-1 border-0 p-0 h-auto text-sm shadow-none focus-visible:ring-0"
                value={pg.timeline.target_go_live}
                onChange={(e) => setProjectGoals({ ...pg, timeline: { ...pg.timeline, target_go_live: e.target.value } })}
                placeholder="e.g., End of Q3 2026"
              />
            </div>
            {pg.timeline.phases.map((phase, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                <Input className="flex-1 border-0 p-0 h-auto text-sm font-medium shadow-none focus-visible:ring-0" value={phase.name} onChange={(e) => updatePhase(i, 'name', e.target.value)} placeholder="Phase name" />
                <Input className="w-[140px] border-0 p-0 h-auto text-sm shadow-none focus-visible:ring-0" value={phase.target_date} onChange={(e) => updatePhase(i, 'target_date', e.target.value)} placeholder="Target date" />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500 shrink-0" onClick={() => removePhase(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addPhase} className="text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Phase
            </Button>
            {/* Go-live urgency (OnboardingFlow step 5) */}
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-3" data-testid="urgency">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">How urgent is go-live?</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {URGENCY_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={clientProfile.urgency === o.value}
                    onClick={() => setClientProfile({ ...clientProfile, urgency: o.value })}
                    className={`rounded-lg border px-3 py-2 text-left transition-colors ${clientProfile.urgency === o.value ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <span className="block text-sm font-semibold text-gray-900">{o.label}</span>
                    <span className="block text-xs text-gray-500">{o.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ========== SECTION: Platform (OnboardingFlow step 4) ========== */}
        <div data-testid="platform-type">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Server className="h-4 w-4 text-gray-500" />
            Platform
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {([['cloud', 'Cloud', 'Hosted by the vendor', Cloud], ['datacenter', 'Data Center', 'Hosted on your own servers', Server]] as const).map(([value, label, hint, Icon]) => (
              <button
                key={value}
                type="button"
                aria-pressed={clientProfile.platformType === value}
                onClick={() => setClientProfile({ ...clientProfile, platformType: value })}
                className={`flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${clientProfile.platformType === value ? 'border-purple-600 bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              >
                <Icon className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{label}</span>
                  <span className="block text-xs text-gray-500">{hint}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ========== SECTION: Confidence Summary ========== */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Data Confidence</h4>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-semibold ${confidenceColor(cs.overall)}`}>
              {cs.overall}
            </Badge>
          </div>
          {cs.warnings.length > 0 && (
            <div className="space-y-1.5 mt-2">
              {cs.warnings.map((warning, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TeamInvitesStep({ 
  teamMembers, 
  addTeamMember, 
  removeTeamMember, 
  updateTeamMember,
  clientProfile,
  setClientProfile,
}: { 
  teamMembers: TeamMember[];
  addTeamMember: () => void;
  removeTeamMember: (index: number) => void;
  updateTeamMember: (index: number, field: keyof TeamMember, value: string) => void;
  clientProfile: ClientProfile;
  setClientProfile: (p: ClientProfile) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Your Team</CardTitle>
        <CardDescription>
          Add team members now or skip and do this later
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Team size and departments (OnboardingFlow step 6): planning training and rollout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="team-shape">
          <div className="space-y-1.5">
            <Label htmlFor="team-size">How many people will use the system?</Label>
            <Select value={clientProfile.teamSize || undefined} onValueChange={(v) => setClientProfile({ ...clientProfile, teamSize: v })}>
              <SelectTrigger id="team-size" aria-label="Team size">
                <SelectValue placeholder="Team size" />
              </SelectTrigger>
              <SelectContent>
                {TEAM_SIZE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o} people</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="departments">Which departments are involved?</Label>
            <Input
              id="departments"
              placeholder="e.g. IT, HR, Facilities"
              value={clientProfile.departmentsInvolved}
              onChange={(e) => setClientProfile({ ...clientProfile, departmentsInvolved: e.target.value })}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500">This helps your consultant plan training and rollout.</p>

        {teamMembers.map((member, index) => (
          <div key={index} className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-4">
              <Input
                placeholder="Full Name"
                value={member.name}
                onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
              />
            </div>
            <div className="col-span-4">
              <Input
                type="email"
                placeholder="email@company.com"
                value={member.email}
                onChange={(e) => updateTeamMember(index, 'email', e.target.value)}
              />
            </div>
            <div className="col-span-3">
              <Select
                value={member.role}
                onValueChange={(value: any) => updateTeamMember(index, 'role', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_member">Team Member</SelectItem>
                  <SelectItem value="engagement_lead">Engagement Lead</SelectItem>
                  <SelectItem value="executive_sponsor">Executive Sponsor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              {teamMembers.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTeamMember(index)}
                  className="h-10 w-10 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addTeamMember}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Team Member
        </Button>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Team members will receive an email invitation to join and collaborate on this project
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

function ReviewStep({ companyName, companyId, accessToken }: { companyName: string; companyId: string; accessToken: string }) {
  // The starting point is the home's first next step (GET /client/home, nextSteps[0]), so this
  // step and the home say the same thing. Before, a hard-coded asset-management sentence was
  // shown to every client, whatever the engagement asked for (walk findings L4, S10).
  const [start, setStart] = useState<NextStep | null | undefined>(undefined); // undefined = looking
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/client/home/${encodeURIComponent(companyId)}`,
          { headers: getAuthHeaders(accessToken) },
        );
        const body = await res.json().catch(() => ({}));
        const first = res.ok && Array.isArray(body.nextSteps) ? body.nextSteps[0] : null;
        if (!cancelled) setStart(first ?? null);
      } catch (error) {
        console.error('[ONBOARDING] starting point lookup failed:', error);
        if (!cancelled) setStart(null);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, accessToken]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Setup Complete</CardTitle>
        <CardDescription>
          Your account is ready for {companyName}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">Setup Complete</AlertTitle>
          <AlertDescription className="text-green-700">
            Your account is ready. Here's what happens next:
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-700">
              1
            </div>
            <div>
              <p className="text-gray-900">Your consultant will review your goals</p>
              <p className="text-sm text-gray-600">
                We'll identify any gaps or clarifications needed
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-700">
              2
            </div>
            <div>
              <p className="text-gray-900">Begin guided discovery</p>
              <p className="text-sm text-gray-600">
                We'll ask targeted questions to gather implementation details
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-700">
              3
            </div>
            <div>
              <p className="text-gray-900">Watch your project take shape</p>
              <p className="text-sm text-gray-600">
                Track progress in real-time as we configure your system
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Target className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-blue-900 mb-1">Your starting point</h4>
              {start === undefined ? (
                <p className="text-sm text-blue-700">Finding the first thing to answer…</p>
              ) : start ? (
                <>
                  <p className="text-sm text-blue-900">{start.text}</p>
                  <p className="text-sm text-blue-700">{categoryLabel(start.category)} · {start.blockName}. It is the first item on your home.</p>
                </>
              ) : (
                <p className="text-sm text-blue-700">Your consultant is preparing your first questions. Your home will show them as soon as they are ready.</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// DRAFT AUTOSAVE UTILITIES
// ============================================
const DRAFT_KEY_PREFIX = 'onboarding_draft_';

interface OnboardingDraft {
  step: number;
  projectGoals: ProjectGoals;
  teamMembers: any[];
  hloData: any;
  clientProfile?: ClientProfile;
  timestamp: number;
}

function saveDraft(companyId: string, draft: OnboardingDraft) {
  try {
    localStorage.setItem(`${DRAFT_KEY_PREFIX}${companyId}`, JSON.stringify(draft));
  } catch (e) {
    console.warn('[ONBOARDING_DRAFT] Failed to save draft:', e);
  }
}

function loadDraft(companyId: string): OnboardingDraft | null {
  try {
    const raw = localStorage.getItem(`${DRAFT_KEY_PREFIX}${companyId}`);
    if (!raw) return null;
    return JSON.parse(raw) as OnboardingDraft;
  } catch {
    return null;
  }
}

function clearDraft(companyId: string) {
  try {
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${companyId}`);
  } catch {}
}